/**
 * Monetização (PLANO-ECONOMIA-MONETIZACAO Parte 4/6). SEM gateway de pagamento aqui —
 * este módulo só manipula a carteira local de Emerald (`state.emerald`) e os produtos
 * premium (boosts/VIP/pass/ads). Crédito real de Emerald por dinheiro é responsabilidade
 * do backend (webhook → RPC server-side). Núcleo puro — sem DOM, sem I/O.
 */

export { EMERALD_PACKAGES } from './emerald-pricing.mjs';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const HOUR_MS = 3600000;
const DAY_MS = 86400000;
export const CURRENT_SEASON_ID = 'season-1';

export function normalizeMonetization(state) {
  if (!state || typeof state !== 'object') return state;
  state.emerald = Math.max(0, Math.floor(Number(state.emerald) || 0));
  const current = state.monetization && typeof state.monetization === 'object' ? state.monetization : {};
  const boosts = current.boosts && typeof current.boosts === 'object' ? current.boosts : {};
  const season = current.season && typeof current.season === 'object' ? current.season : {};
  state.monetization = {
    emeraldHistory: Array.isArray(current.emeraldHistory) ? current.emeraldHistory : [],
    boosts: {
      pending: Array.isArray(boosts.pending) ? boosts.pending : [],
      active: boosts.active && typeof boosts.active === 'object' ? boosts.active : {},
      dailyCount: boosts.dailyCount && typeof boosts.dailyCount === 'object' ? boosts.dailyCount : {},
    },
    vip: current.vip && typeof current.vip === 'object' ? current.vip : null,
    season: {
      id: season.id === CURRENT_SEASON_ID ? season.id : CURRENT_SEASON_ID,
      premium: season.id === CURRENT_SEASON_ID && !!season.premium,
      claimed: {
        free: season.id === CURRENT_SEASON_ID && Array.isArray(season.claimed?.free) ? season.claimed.free : [],
        premium: season.id === CURRENT_SEASON_ID && Array.isArray(season.claimed?.premium) ? season.claimed.premium : [],
      },
    },
    lastAdAt: Math.max(0, Number(current.lastAdAt) || 0),
  };
  return state;
}

// --- 4.1 Pacotes de Emerald ---
// A compra por dinheiro existe apenas no backend: Stripe Checkout -> webhook assinado -> RPC.

// --- 4.2 Boosts temporários ---

export const BOOST_DEFS = Object.freeze({
  trainerXp: Object.freeze({ key: 'trainerXp', label: 'XP Treinador', mult: 1.5, pricePerHour: 3 }),
  pokemonXp: Object.freeze({ key: 'pokemonXp', label: 'XP Pokémon', mult: 1.5, pricePerHour: 3 }),
  loot: Object.freeze({ key: 'loot', label: 'Loot', mult: 1.4, pricePerHour: 6 }),
  capture: Object.freeze({ key: 'capture', label: 'Captura', mult: 2, pricePerHour: 6 }),
  shinyLure: Object.freeze({ key: 'shinyLure', label: 'Isca Shiny', mult: 2, pricePerHour: 6 }),
  idlecoin: Object.freeze({ key: 'idlecoin', label: 'IdleCoin', mult: 1.5, pricePerHour: 6 }),
});

/** Descontos por duração — mesma mecânica do PokeIdle (comprovada). */
export const BOOST_DURATION_DISCOUNTS = Object.freeze([
  Object.freeze({ hours: 1, discount: 0 }),
  Object.freeze({ hours: 3, discount: 0.20 }),
  Object.freeze({ hours: 6, discount: 0.35 }),
  Object.freeze({ hours: 12, discount: 0.45 }),
  Object.freeze({ hours: 24, discount: 0.60 }),
  Object.freeze({ hours: 168, discount: 0.45 }),
]);

export const BOOST_DAILY_LIMIT = 5;

function discountForHours(hours) {
  const entry = BOOST_DURATION_DISCOUNTS.find((row) => row.hours === Number(hours));
  return entry ? entry.discount : 0;
}

export function boostPrice(key, hours) {
  const def = BOOST_DEFS[key];
  if (!def) return Infinity;
  const safeHours = Math.max(1, Number(hours) || 1);
  const discount = discountForHours(safeHours);
  return Math.max(1, Math.round(def.pricePerHour * safeHours * (1 - discount)));
}

const dayKey = (ts) => Math.floor(Number(ts) / DAY_MS);

function dailyCountFor(state, key, now) {
  const entry = state.monetization.boosts.dailyCount[key];
  return entry && entry.day === dayKey(now) ? entry.count : 0;
}

/** Compra um boost — fica pendente no Gift Center até `activateBoost`. */
export function buyBoost(state, key, hours, now = Date.now(), options = {}) {
  const def = BOOST_DEFS[key];
  if (!state || typeof state !== 'object' || !def) return { ok: false, error: 'invalid_boost' };
  normalizeMonetization(state);
  if (dailyCountFor(state, key, now) >= BOOST_DAILY_LIMIT) return { ok: false, error: 'daily_limit_reached' };
  const price = boostPrice(key, hours);
  if (!options.serverDebited && state.emerald < price) return { ok: false, error: 'insufficient_emerald' };
  if (!options.serverDebited) state.emerald -= price;
  state.monetization.boosts.pending.push({
    id: `${key}-${now}-${Math.floor(Math.random() * 1e6)}`, key, hours: Math.max(1, Number(hours) || 1), purchasedAt: now,
  });
  const count = dailyCountFor(state, key, now) + 1;
  state.monetization.boosts.dailyCount[key] = { day: dayKey(now), count };
  return { ok: true, cost: price, pendingCount: state.monetization.boosts.pending.length };
}

/** Ativa o boost pendente mais antigo daquele tipo. Sem stack do mesmo tipo. */
export function activateBoost(state, key, now = Date.now()) {
  if (!state || typeof state !== 'object' || !BOOST_DEFS[key]) return { ok: false, error: 'invalid_boost' };
  normalizeMonetization(state);
  const active = state.monetization.boosts.active[key];
  if (active && active.expiresAt > now) return { ok: false, error: 'already_active' };
  const pending = state.monetization.boosts.pending;
  const index = pending.findIndex((entry) => entry.key === key);
  if (index < 0) return { ok: false, error: 'no_pending_boost' };
  const [entry] = pending.splice(index, 1);
  const expiresAt = now + entry.hours * HOUR_MS;
  state.monetization.boosts.active[key] = { expiresAt, mult: BOOST_DEFS[key].mult };
  return { ok: true, expiresAt };
}

/** Multiplicador ativo (1 se não houver boost vigente daquele tipo). */
export function activeBoostMult(state, key, now = Date.now()) {
  const active = state?.monetization?.boosts?.active?.[key];
  if (!active || active.expiresAt <= now) return 1;
  return active.mult;
}

// --- 4.3 VIP (assinatura) ---

export const VIP_PLANS = Object.freeze([
  Object.freeze({ days: 30, cost: 15 }),
  Object.freeze({ days: 60, cost: 25 }),
  Object.freeze({ days: 90, cost: 35 }),
]);

/** Ativa/estende o VIP. Se já houver VIP vigente, o novo plano soma ao tempo restante. */
export function activateVip(state, planDays, now = Date.now(), options = {}) {
  const plan = VIP_PLANS.find((entry) => entry.days === Number(planDays));
  if (!state || typeof state !== 'object' || !plan) return { ok: false, error: 'invalid_plan' };
  normalizeMonetization(state);
  if (!options.serverDebited && state.emerald < plan.cost) return { ok: false, error: 'insufficient_emerald' };
  if (!options.serverDebited) state.emerald -= plan.cost;
  const base = state.monetization.vip?.expiresAt > now ? state.monetization.vip.expiresAt : now;
  state.monetization.vip = { expiresAt: base + plan.days * DAY_MS };
  return { ok: true, expiresAt: state.monetization.vip.expiresAt };
}

export function applyVipEntitlement(state, expiresAt) {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMonetization(state);
  const safeExpiry = Math.max(0, Number(expiresAt) || 0);
  state.monetization.vip = safeExpiry > Date.now() ? { expiresAt: safeExpiry } : null;
  return { ok: true, expiresAt: safeExpiry };
}

export function isVip(state, now = Date.now()) {
  return (state?.monetization?.vip?.expiresAt || 0) > now;
}

/** Perks fixos do VIP — conveniência/cosmético, nunca poder direto de Market. */
export function vipPerks() {
  return Object.freeze({
    marketFeeRate: 0.01,
    extraMarketSlots: 1,
    extraBoxSlots: 1,
    boostQueueSlots: 1,
    offlineCapBonusHours: 4,
    autoRestock: true,
    chatBadge: true,
    trainerXpMultiplier: 1.25,
    pokemonXpMultiplier: 1.25,
    lootMultiplier: 1.25,
    /** Único caminho de EXP para quem não está equipado como parceiro. */
    reserveXpShare: 0.1,
    /** Auto-capture é automação exclusiva do VIP. */
    autoCapture: true,
    shinyMultiplier: 1.1,
    questRerolls: 1,
  });
}

/** Ofertas da loja Emerald: bolas premium para especiais de zona. */
export const EMERALD_BALL_OFFERS = Object.freeze([
  Object.freeze({ id: 'apexball-1', slug: 'apexball', qty: 1, cost: 8, label: 'Apex Ball' }),
  Object.freeze({ id: 'apexball-3', slug: 'apexball', qty: 3, cost: 20, label: 'Apex Ball ×3' }),
  Object.freeze({ id: 'masterball-1', slug: 'masterball', qty: 1, cost: 30, label: 'Master Ball' }),
  Object.freeze({ id: 'masterball-3', slug: 'masterball', qty: 3, cost: 80, label: 'Master Ball ×3' }),
]);

/** Compra bola premium com Emerald. */
export function buyEmeraldBall(state, offerId, options = {}) {
  const offer = EMERALD_BALL_OFFERS.find((entry) => entry.id === offerId);
  if (!state || typeof state !== 'object' || !offer) return { ok: false, error: 'invalid_offer' };
  normalizeMonetization(state);
  if (!options.serverDebited && state.emerald < offer.cost) return { ok: false, error: 'insufficient_emerald' };
  if (!options.serverDebited) state.emerald -= offer.cost;
  state.inventory = state.inventory && typeof state.inventory === 'object' ? state.inventory : {};
  state.inventory[offer.slug] = (Number(state.inventory[offer.slug]) || 0) + offer.qty;
  state.monetization.emeraldHistory.push({
    kind: 'ball_purchase', offerId, slug: offer.slug, qty: offer.qty, cost: offer.cost, at: Date.now(),
  });
  return { ok: true, slug: offer.slug, qty: offer.qty, cost: offer.cost, balance: state.emerald };
}

// --- 4.4 Game Pass (stub de temporada) ---

export const SEASON_TIER_COUNT = 50;
export const SEASON_PREMIUM_COST = 15;

const item = (slug, qty) => Object.freeze({ type: 'item', slug, qty });
const coins = (amount) => Object.freeze({ type: 'coins', amount });
const emerald = (amount) => Object.freeze({ type: 'emerald', amount });
const boost = (key, hours) => Object.freeze({ type: 'boost', key, hours });

const FREE_SEASON_REWARDS = Object.freeze([
  item('pokeball', 3), coins(400), item('potion', 2), item('pokeball', 5), boost('trainerXp', 1),
  coins(700), item('greatball', 2), item('great-potion', 1), item('pokeball', 6), emerald(1),
  item('greatball', 3), coins(1000), item('potion', 4), boost('pokemonXp', 1), item('superball', 1),
  coins(1400), item('greatball', 4), item('great-potion', 2), item('pokeball', 8), emerald(1),
  boost('loot', 1), coins(1800), item('superball', 2), item('revive', 1), item('greatball', 5),
  coins(2200), item('great-potion', 3), boost('capture', 1), item('ultraball', 1), emerald(2),
  item('superball', 3), coins(2800), item('revive', 2), item('greatball', 7), boost('shinyLure', 1),
  coins(3400), item('ultraball', 2), item('great-potion', 4), item('superball', 4), emerald(2),
  boost('trainerXp', 3), coins(4200), item('revive', 3), item('ultraball', 3), item('superball', 6),
  coins(5000), boost('pokemonXp', 3), item('ultraball', 4), emerald(3), item('apexball', 1),
]);

const PREMIUM_SEASON_REWARDS = Object.freeze([
  item('greatball', 5), boost('trainerXp', 1), coins(1000), item('superball', 2), item('revive', 1),
  boost('pokemonXp', 1), item('great-potion', 3), coins(1800), item('ultraball', 1), emerald(2),
  boost('loot', 1), item('superball', 4), coins(2400), boost('capture', 1), item('ultraball', 2),
  emerald(2), item('revive', 2), boost('shinyLure', 1), coins(3200), item('apexball', 1),
  boost('trainerXp', 3), item('ultraball', 3), coins(4000), boost('pokemonXp', 3), emerald(3),
  item('superball', 8), boost('loot', 3), item('revive', 3), coins(5000), item('apexball', 1),
  boost('capture', 3), item('ultraball', 5), emerald(3), boost('shinyLure', 3), coins(6500),
  item('great-potion', 8), boost('trainerXp', 6), item('apexball', 1), emerald(4), boost('pokemonXp', 6),
  item('revive', 5), coins(8000), boost('loot', 6), item('ultraball', 8), emerald(5),
  boost('capture', 6), item('apexball', 2), boost('shinyLure', 6), coins(12000), emerald(8),
]);

export const SEASON_TIERS = Object.freeze(Array.from({ length: SEASON_TIER_COUNT }, (_, index) => Object.freeze({
  tier: index + 1,
  pointsRequired: (index + 1) * 100,
  free: FREE_SEASON_REWARDS[index],
  premium: PREMIUM_SEASON_REWARDS[index],
})));

export function buyPremiumPass(state, options = {}) {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMonetization(state);
  if (state.monetization.season.premium) return { ok: false, error: 'already_premium' };
  if (!options.serverDebited && state.emerald < SEASON_PREMIUM_COST) return { ok: false, error: 'insufficient_emerald' };
  if (!options.serverDebited) state.emerald -= SEASON_PREMIUM_COST;
  state.monetization.season.premium = true;
  return { ok: true };
}

/** Reivindica um tier do pass usando `state.meta.passPoints` (camada meta é a fonte). */
export function claimTier(state, tier, track = 'free') {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMonetization(state);
  const definition = SEASON_TIERS[clamp(Math.floor(Number(tier) || 0) - 1, 0, SEASON_TIER_COUNT - 1)];
  if (!definition || definition.tier !== Number(tier)) return { ok: false, error: 'invalid_tier' };
  if (track === 'premium' && !state.monetization.season.premium) return { ok: false, error: 'premium_required' };
  const passPoints = Math.max(0, Math.floor(Number(state?.meta?.passPoints) || 0));
  if (passPoints < definition.pointsRequired) return { ok: false, error: 'not_enough_points' };
  const claimedList = state.monetization.season.claimed[track === 'premium' ? 'premium' : 'free'];
  if (claimedList.includes(definition.tier)) return { ok: false, error: 'already_claimed' };
  claimedList.push(definition.tier);
  return { ok: true, reward: track === 'premium' ? definition.premium : definition.free };
}

// --- 4.7 Rewarded ads ---

export const REWARDED_AD_COOLDOWN_MS = 4 * HOUR_MS;
export const REWARDED_AD_EMERALD = 2;

/** Vídeo recompensado opcional: +2 Emerald a cada 4h. Nunca obrigatório. */
export function watchRewardedAd(state, now = Date.now()) {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMonetization(state);
  const elapsed = now - state.monetization.lastAdAt;
  if (state.monetization.lastAdAt && elapsed < REWARDED_AD_COOLDOWN_MS) {
    return { ok: false, error: 'cooldown', retryAt: state.monetization.lastAdAt + REWARDED_AD_COOLDOWN_MS };
  }
  state.monetization.lastAdAt = now;
  state.emerald += REWARDED_AD_EMERALD;
  return { ok: true, emerald: REWARDED_AD_EMERALD, balance: state.emerald };
}
